/**
 * CPU reference of the Hillaire/Bruneton Earth atmosphere (no three.js). Used for the sun light colour on the CPU
 * and as the source of truth for the GLSL constants in atmosphereShaders.ts (same numbers, same units: metres).
 */
import type { RGB } from '../skyPalette';

export const ATMOSPHERE = {
  groundRadiusM: 6_360_000,
  topRadiusM: 6_460_000,
  rayleighScattering: [5.802e-6, 13.558e-6, 33.1e-6] as RGB,
  rayleighScaleHeightM: 8_000,
  mieScattering: 3.996e-6,
  mieExtinction: 4.4e-6,
  mieScaleHeightM: 1_200,
  mieG: 0.8,
  ozoneAbsorption: [0.65e-6, 1.881e-6, 0.085e-6] as RGB,
  ozoneCenterM: 25_000,
  ozoneHalfWidthM: 15_000,
} as const;

/** Extinction coefficient per metre at a height above the ground. */
export function extinctionAt(heightM: number): RGB {
  const a = ATMOSPHERE;
  const r = Math.exp(-heightM / a.rayleighScaleHeightM);
  const m = Math.exp(-heightM / a.mieScaleHeightM);
  const o = Math.max(0, 1 - Math.abs(heightM - a.ozoneCenterM) / a.ozoneHalfWidthM);
  return [0, 1, 2].map((i) => a.rayleighScattering[i]! * r + a.mieExtinction * m + a.ozoneAbsorption[i]! * o) as RGB;
}

/** Distance along a ray from radius r with cos(zenith) mu to a sphere of radius R (or -1 if it misses). */
function rayToSphere(r: number, mu: number, R: number): number {
  const disc = r * r * (mu * mu - 1) + R * R;
  if (disc < 0) return -1;
  const s = Math.sqrt(disc);
  const far = -r * mu + s;
  return far >= 0 ? far : -1;
}

/** Transmittance from a point at heightM towards the top of the atmosphere; 0 if the ray hits the ground. */
export function transmittanceToSpace(heightM: number, cosZenith: number, steps = 64): RGB {
  const a = ATMOSPHERE;
  const r = a.groundRadiusM + Math.max(0, heightM);
  const toGroundDisc = r * r * (cosZenith * cosZenith - 1) + a.groundRadiusM * a.groundRadiusM;
  if (cosZenith < 0 && toGroundDisc >= 0) return [0, 0, 0];
  const length = rayToSphere(r, cosZenith, a.topRadiusM);
  const dt = length / steps;
  const tau: RGB = [0, 0, 0];
  for (let i = 0; i < steps; i++) {
    const t = (i + 0.5) * dt;
    const h = Math.sqrt(r * r + t * t + 2 * r * t * cosZenith) - a.groundRadiusM;
    const e = extinctionAt(h);
    tau[0] += e[0] * dt;
    tau[1] += e[1] * dt;
    tau[2] += e[2] * dt;
  }
  return [Math.exp(-tau[0]), Math.exp(-tau[1]), Math.exp(-tau[2])];
}

/** Sun light colour reaching an observer at heightM with the sun at altitude sunAltDeg (linear RGB, max 1). */
export function sunTransmittance(sunAltDeg: number, heightM = 80): RGB {
  return transmittanceToSpace(heightM, Math.sin((sunAltDeg * Math.PI) / 180));
}
